import React from 'react'
import {Helmet} from 'react-helmet'
import {useIntl} from 'react-intl'
import type {MetaTagsProps} from '_types'

/**
 * @name MetaTags
 *
 * @component
 *
 * @description
 * Sets the page's metadata including title, description, keywords metadata, JSON-LD script and robots.
 *
 * Alternate/canonical tags are rendered in a separate `<Helmet>` instance that is mounted
 * only when the data is available. This prevents react-helmet from stripping SSR-rendered
 * hreflang/canonical tags during SPA navigations while the new page's data is still loading.
 *
 * `enableNoIndex` controls the robots meta tag independently.
 * `hideAlternateUrls` hides hreflang alternates without affecting the canonical URL.
 * Use `hideAlternateUrls` for filtered/sorted PLP pages that keep their canonical but
 * should not expose alternate language versions.
 */
const MetaTags = ({
    title,
    description,
    keywords,
    jsonLd,
    enableNoIndex = false,
    hideAlternateUrls = false,
    alternateUrls,
    canonicalUrl,
}: MetaTagsProps): JSX.Element => {
    const {formatMessage} = useIntl()

    const formattedTitle = typeof title === 'string' ? title : formatMessage(title)
    const formattedDescription = typeof description === 'string' ? description : formatMessage(description)
    const formattedKeywords = keywords || formatMessage({id: 'global.storename', defaultMessage: 'Brunello Cucinelli - official boutique'})

    // Alternate URLs: hidden when noindex OR when explicitly suppressed (e.g. filtered pages)
    const showAlternateUrls = !enableNoIndex && !hideAlternateUrls && alternateUrls && alternateUrls.length > 0
    // Canonical URL: shown independently of noindex (a noindex page can still have a canonical)
    const showCanonicalUrl = !!canonicalUrl

    return (
        <>
            {/* Primary meta: always rendered, updates immediately on page change */}
            <Helmet>
                <title>{formattedTitle}</title>

                <meta name="description" content={formattedDescription} />

                <meta name="keywords" content={keywords || formattedKeywords} />

                {jsonLd && typeof jsonLd === 'string' && <script type="application/ld+json">{jsonLd}</script>}

                {jsonLd &&
                    Array.isArray(jsonLd) &&
                    jsonLd.map((jsonLdItem, index) => (
                        <script key={index} type="application/ld+json">
                            {jsonLdItem}
                        </script>
                    ))}

                {enableNoIndex && <meta name="robots" content="noindex, nofollow" />}
            </Helmet>

            {/* SEO link tags: mounted only when data is available */}
            {(showAlternateUrls || showCanonicalUrl) && (
                <Helmet>
                    {showAlternateUrls &&
                        alternateUrls!.map((alternateUrl, index) => (
                            <link key={`${alternateUrl.hreflang}-${index}`} rel="alternate" hrefLang={alternateUrl.hreflang} href={alternateUrl.url} />
                        ))}

                    {showCanonicalUrl && <link rel="canonical" href={canonicalUrl} />}
                </Helmet>
            )}
        </>
    )
}

export {MetaTags}
